/** Phase 1 device-recorder check: the pure logic (decision 0051). No DOM, no network -- tested with
 * Node (test/device-check.test.ts). The page (device-check.ts) feeds these functions what it measured and
 * renders the grid they return. Test only; synthetic audio only. */

export type Track = "mic" | "call";
export type Verdict = "pass" | "fail" | "info" | "not_tested";

/** The first of these the browser can record is used. Chrome and Edge give webm/opus, Safari mp4/AAC. */
export const MIME_CANDIDATES = ["audio/webm;codecs=opus", "audio/mp4", "audio/ogg;codecs=opus", "audio/webm"] as const;

export function pickMimeType(isSupported: (mime: string) => boolean): string | null {
  for (const mime of MIME_CANDIDATES) if (isSupported(mime)) return mime;
  return null;
}

/** Root-mean-square of an analyser's time-domain bytes (0..255, 128 = silence) as 0..1. */
export function levelFromBytes(bytes: ArrayLike<number>): number {
  if (bytes.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < bytes.length; i++) {
    const v = (bytes[i]! - 128) / 128;
    sum += v * v;
  }
  return Math.sqrt(sum / bytes.length);
}

export function median(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

export interface LevelSample {
  t: number; // seconds since recording started
  mic: number;
  call: number;
}

/** Sound is "present" above this RMS. Chosen above the noise floor of a quiet room, not tuned further. */
export const SOUND_THRESHOLD = 0.02;

export interface EchoResult {
  verdict: Verdict;
  detail: string;
  micMedian: number | null;
  callMedian: number | null;
}

/** The echo test: in a window where the tester stays silent and the call is playing, a mic that is
 * hearing the call's speaker shows level too. `notTested` when no such window has call sound. */
export function echoVerdict(samples: readonly LevelSample[], fromS: number, toS: number): EchoResult {
  const inWindow = samples.filter((s) => s.t >= fromS && s.t <= toS && s.call > SOUND_THRESHOLD);
  if (inWindow.length < 8) {
    return {
      verdict: "not_tested",
      detail: "No call sound in the quiet window, so echo could not be judged.",
      micMedian: null,
      callMedian: null,
    };
  }
  const mic = median(inWindow.map((s) => s.mic));
  const call = median(inWindow.map((s) => s.call));
  const heard = mic > SOUND_THRESHOLD && mic > 0.15 * call;
  return {
    verdict: heard ? "fail" : "pass",
    detail: heard
      ? `The mic heard the call while you were silent (mic ${mic.toFixed(3)} vs call ${call.toFixed(3)}). Expect doubled words without headphones.`
      : `The mic stayed quiet while the call played (mic ${mic.toFixed(3)} vs call ${call.toFixed(3)}).`,
    micMedian: mic,
    callMedian: call,
  };
}

export interface LogEvent {
  t: number;
  type: string;
  detail?: string;
}

/** Spans (seconds) where the page was hidden or frozen, from the event log. An unfinished span runs to `endT`. */
export function hiddenSpans(events: readonly LogEvent[], endT: number): Array<{ from: number; to: number }> {
  const spans: Array<{ from: number; to: number }> = [];
  let open: number | null = null;
  for (const e of events) {
    const hides = (e.type === "visibility" && e.detail === "hidden") || e.type === "freeze" || e.type === "pagehide";
    const shows = (e.type === "visibility" && e.detail === "visible") || e.type === "resume" || e.type === "pageshow";
    if (hides && open === null) open = e.t;
    else if (shows && open !== null) {
      spans.push({ from: open, to: e.t });
      open = null;
    }
  }
  if (open !== null) spans.push({ from: open, to: endT });
  return spans;
}

/** Gaps between consecutive chunk arrival times larger than `factor` x the expected slice. */
export function chunkGaps(arrivals: readonly number[], sliceS: number, factor = 2): number[] {
  const gaps: number[] = [];
  for (let i = 1; i < arrivals.length; i++) {
    const gap = arrivals[i]! - arrivals[i - 1]!;
    if (gap > sliceS * factor) gaps.push(Math.round(gap * 10) / 10);
  }
  return gaps;
}

export interface TrackStats {
  present: boolean; // a track existed at all (getDisplayMedia gave an audio track / the mic opened)
  chunks: number; // chunks the recorder produced
  uploaded: number; // chunks stored in S3 as far as the browser knows
  bytes: number;
  maxLevel: number;
  arrivals: number[];
  endedEarly: boolean;
}

export interface DropStats {
  simulated: boolean;
  offlineEvents: number;
  maxQueue: number;
  retries: number;
  clearedAfterS: number | null; // seconds from the end of the drop until the queue was empty
}

export interface VerifyTrack {
  chunks: number;
  bytes: number;
  container: string; // what chunk 0's header says
  audio_codecs: string[];
  video_codecs: string[];
  contiguous: boolean; // chunk numbers run 0..n-1 with no gap
  missing: number[];
  audio_only: boolean; // an audio codec declared and no video codec
}

export interface RunFacts {
  mode: "online" | "in_person";
  sliceS: number;
  durationS: number;
  mimeType: string | null;
  mic: TrackStats;
  call: TrackStats;
  displayVideoTracksReturned: number;
  videoTracksKept: number;
  displaySurface: string | null;
  callAudioTracksReturned: number;
  drop: DropStats;
  events: LogEvent[];
  wakeLockSupported: boolean;
  wakeLockHeld: boolean;
  echo: EchoResult | null;
  serverVerify: { tracks: Record<string, VerifyTrack>; all_audio_only: boolean | null } | null;
  unsentChunks: number; // still waiting in the browser when the run ended
  sessionLostAtS: number | null; // seconds into the run when the console said "sign in" (401), or null
  sessionRecovered: boolean; // the tester signed in again and the queued chunks went up
}

export interface GridRow {
  id: string;
  label: string;
  verdict: Verdict;
  detail: string;
}

const secs = (n: number): string => `${Math.round(n * 10) / 10} s`;

/** The results grid for one device run. Honest about what was not exercised: `not_tested` is not a pass. */
export function buildGrid(f: RunFacts): GridRow[] {
  const rows: GridRow[] = [];

  rows.push({
    id: "mic",
    label: "Your microphone as its own track",
    verdict: f.mic.present && f.mic.chunks > 0 ? (f.mic.maxLevel > SOUND_THRESHOLD ? "pass" : "info") : "fail",
    detail: !f.mic.present
      ? "The microphone could not be opened."
      : f.mic.maxLevel > SOUND_THRESHOLD
        ? `${f.mic.chunks} chunks, loudest level ${f.mic.maxLevel.toFixed(2)}.`
        : `${f.mic.chunks} chunks, but it stayed silent. Speak during the check.`,
  });

  if (f.mode === "online") {
    let verdict: Verdict;
    let detail: string;
    if (f.callAudioTracksReturned === 0) {
      verdict = "fail";
      detail =
        "The browser returned no call sound. This browser or share choice cannot capture it: tick 'share tab audio' or 'share system audio', or it is unsupported here.";
    } else if (f.call.chunks === 0) {
      verdict = "fail";
      detail = "A call-sound track opened but produced no chunks.";
    } else if (f.call.maxLevel > SOUND_THRESHOLD) {
      verdict = "pass";
      detail = `${f.call.chunks} chunks, loudest level ${f.call.maxLevel.toFixed(2)}${f.displaySurface ? `, shared ${f.displaySurface}` : ""}.`;
    } else {
      verdict = "info";
      detail = "A call-sound track recorded, but it stayed silent. Play the call's sound during the check.";
    }
    rows.push({ id: "call", label: "Call sound as its own track", verdict, detail });
  }

  const verifyAudio = f.serverVerify?.all_audio_only;
  const videoOk = f.videoTracksKept === 0 && (f.mimeType ?? "").startsWith("audio/");
  rows.push({
    id: "audio-only",
    label: "Uploads are audio only (video discarded)",
    verdict: videoOk && verifyAudio === true ? "pass" : videoOk && verifyAudio == null ? "info" : "fail",
    detail: `The screen share returned ${f.displayVideoTracksReturned} video track(s); ${f.videoTracksKept} kept. Recorder type ${f.mimeType ?? "none"}. ` +
      (f.serverVerify
        ? `Stored, by each track's first chunk: ${Object.entries(f.serverVerify.tracks).map(([t, v]) => `${t} ${v.container} audio ${v.audio_codecs.join("/") || "none"} video ${v.video_codecs.join("/") || "none"} (${v.chunks} chunks, ${Math.round(v.bytes / Math.max(1, v.chunks))} B each)`).join("; ") || "none"}.`
        : "Storage was not checked."),
  });

  const produced = f.mic.chunks + f.call.chunks;
  const stored = f.serverVerify
    ? Object.values(f.serverVerify.tracks).reduce((n, t) => n + t.chunks, 0)
    : f.mic.uploaded + f.call.uploaded;
  const gaps = f.serverVerify
    ? Object.entries(f.serverVerify.tracks).filter(([, v]) => !v.contiguous).map(([t, v]) => `${t} missing ${v.missing.join(",") || "chunk 0"}`)
    : [];
  rows.push({
    id: "upload",
    label: "Chunks reached storage during the meeting",
    verdict: produced > 0 && stored === produced && gaps.length === 0 ? "pass" : "fail",
    detail:
      `${stored} of ${produced} chunks stored (${f.mic.uploaded} mic and ${f.call.uploaded} call confirmed by the browser).` +
      (f.unsentChunks > 0 ? ` ${f.unsentChunks} were still waiting in the browser when the run ended.` : "") +
      (gaps.length > 0 ? ` Gaps in the numbering: ${gaps.join("; ")}.` : ""),
  });

  rows.push({
    id: "session",
    label: "Console sign-in lasts the whole recording",
    verdict: f.sessionLostAtS === null ? "pass" : f.sessionRecovered ? "info" : "fail",
    detail:
      f.sessionLostAtS === null
        ? `Signed in throughout (${secs(f.durationS)}).`
        : f.sessionRecovered
          ? `The console ended the sign-in ${secs(f.sessionLostAtS)} into the recording; after signing in again the waiting chunks went up. A long recording still needs its own upload authority.`
          : `The console ended the sign-in ${secs(f.sessionLostAtS)} into the recording; uploads were refused from then on. Console sign-ins end about an hour after sign-in whatever the activity, so a long recording needs its own upload authority.`,
  });

  rows.push({
    id: "resume",
    label: "Upload resumes after a dropped connection",
    verdict: f.drop.simulated || f.drop.offlineEvents > 0 ? (f.drop.clearedAfterS !== null && produced === stored ? "pass" : "fail") : "not_tested",
    detail:
      f.drop.simulated || f.drop.offlineEvents > 0
        ? `Queue peaked at ${f.drop.maxQueue} chunks, ${f.drop.retries} retries; ` +
          (f.drop.clearedAfterS !== null ? `empty ${secs(f.drop.clearedAfterS)} after the drop ended.` : "it did not empty.")
        : "Press 'Simulate a dropped connection' during the check.",
  });

  const spans = hiddenSpans(f.events, f.durationS);
  const longest = spans.reduce((m, s) => Math.max(m, s.to - s.from), 0);
  const pauses = [...chunkGaps(f.mic.arrivals, f.sliceS), ...chunkGaps(f.call.arrivals, f.sliceS)];
  const ended = f.mic.endedEarly || f.call.endedEarly;
  rows.push({
    id: "lock",
    label: "Screen lock or hidden tab",
    verdict: spans.length === 0 ? "not_tested" : ended ? "fail" : pauses.length === 0 ? "pass" : "info",
    detail:
      spans.length === 0
        ? "The page was never hidden. Lock the screen (or switch tab) for 20 seconds during the check."
        : ended
          ? `Hidden for up to ${secs(longest)} and a track ended: recording does not survive this here. Treat a lock as a gap and resume.`
          : pauses.length === 0
            ? `Hidden for up to ${secs(longest)} and recording kept going.`
            : `Hidden for up to ${secs(longest)}; chunks paused (gaps of ${pauses.join(", ")} s) but the tracks did not end.`,
  });

  rows.push({
    id: "wake",
    label: "Keep the screen awake (wake lock)",
    verdict: !f.wakeLockSupported ? "info" : f.wakeLockHeld ? "pass" : "info",
    detail: !f.wakeLockSupported ? "Not supported by this browser." : f.wakeLockHeld ? "Held during the check." : "Supported, not held.",
  });

  if (f.mode === "online") {
    rows.push({
      id: "echo",
      label: "Echo without headphones",
      verdict: f.echo?.verdict ?? "not_tested",
      detail: f.echo?.detail ?? "Tick 'I am not on headphones', stay silent for 20 seconds while the call plays.",
    });
  }
  return rows;
}

/** A coarse device label from the user agent: OS family and browser family, no versions beyond the major. */
export function deviceLabel(ua: string): string {
  const os = /Windows/i.test(ua)
    ? "Windows"
    : /iPhone|iPad|iPod/i.test(ua)
      ? "iOS"
      : /Android/i.test(ua)
        ? "Android"
        : /Mac OS X|Macintosh/i.test(ua)
          ? "macOS"
          : /Linux/i.test(ua)
            ? "Linux"
            : "Unknown OS";
  const m =
    /(Edg)\/(\d+)/.exec(ua) ?? /(OPR)\/(\d+)/.exec(ua) ?? /(Chrome|CriOS)\/(\d+)/.exec(ua) ?? /Version\/(\d+)[^]*Safari/.exec(ua) ?? /(Firefox|FxiOS)\/(\d+)/.exec(ua);
  let browser = "Unknown browser";
  if (m) {
    const name = m[1] === "Edg" ? "Edge" : m[1] === "OPR" ? "Opera" : m[1] === "CriOS" ? "Chrome" : m[1] === "FxiOS" ? "Firefox" : m[1];
    if (/Version\/(\d+)[^]*Safari/.test(ua) && !/(Chrome|CriOS|Edg|OPR)\//.test(ua)) browser = `Safari ${/Version\/(\d+)/.exec(ua)![1]}`;
    else browser = `${name} ${m[2] ?? ""}`.trim();
  }
  return `${os} · ${browser}`;
}
